import { useState, useEffect } from 'react'
import { collection, getDocs, query, orderBy, doc, updateDoc, limit } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from '../context/AuthContext'
import baseTimestamps from '../data/word-timestamps.json'
import TeachersTab from './admin/TeachersTab'

const TABS = ['Overview', 'Users', 'Analytics', 'Feedback', 'Timestamps', 'Teachers']
const CATEGORY_ICONS = { general: '\uD83D\uDCAC', bug: '\uD83D\uDC1B', feature: '\uD83D\uDCA1', content: '\uD83D\uDCDD' }

export default function AdminDashboard({ onClose }) {
  const { isAdmin } = useAuth()
  const [activeTab, setActiveTab] = useState('Overview')
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [feedbackList, setFeedbackList] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAdmin) return
    loadData()
  }, [isAdmin])

  const [activityData, setActivityData] = useState([])
  const [usageStatsData, setUsageStatsData] = useState([])
  const [timestampDocs, setTimestampDocs] = useState([])

  async function loadData() {
    setLoading(true)
    try {
      const [usersSnap, feedbackSnap, activitySnap, usageStatsSnap, timestampsSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(200))),
        getDocs(query(collection(db, 'feedback'), orderBy('createdAt', 'desc'), limit(200))),
        getDocs(query(collection(db, 'activity'), orderBy('createdAt', 'desc'), limit(500))),
        getDocs(collection(db, 'usageStats')),
        getDocs(collection(db, 'wordTimestamps'))
      ])
      setTimestampDocs(timestampsSnap.docs.map(d => ({ id: d.id, ...d.data() })))

      const usersData = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      const feedbackData = feedbackSnap.docs.map(d => ({ id: d.id, ...d.data() }))

      const actData = activitySnap.docs.map(d => ({ id: d.id, ...d.data() }))
      const usageData = usageStatsSnap.docs.map(d => ({ id: d.id, ...d.data() }))

      setUsers(usersData)
      setFeedbackList(feedbackData)
      setActivityData(actData)
      setUsageStatsData(usageData)

      const now = new Date()
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const recentUsers = usersData.filter(u => u.createdAt && new Date(u.createdAt) > thirtyDaysAgo)

      const ratedFeedback = feedbackData.filter(f => f.rating > 0)
      const avgRating = ratedFeedback.length > 0
        ? (ratedFeedback.reduce((sum, f) => sum + f.rating, 0) / ratedFeedback.length).toFixed(1)
        : 'N/A'

      const premiumCount = usersData.filter(u => {
        const sub = u.subscription || {}
        return sub.status === 'active'
      }).length

      const unresolvedCount = feedbackData.filter(f => !f.resolved).length

      setStats({
        totalUsers: usersData.length,
        recentUsers: recentUsers.length,
        premiumUsers: premiumCount,
        totalFeedback: feedbackData.length,
        unresolvedFeedback: unresolvedCount,
        avgRating
      })
    } catch (err) {
      console.error('Failed to load admin data:', err)
    } finally {
      setLoading(false)
    }
  }

  async function toggleResolved(feedbackId, currentResolved) {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), { resolved: !currentResolved })
      setFeedbackList(prev =>
        prev.map(f => f.id === feedbackId ? { ...f, resolved: !currentResolved } : f)
      )
      setStats(prev => prev ? {
        ...prev,
        unresolvedFeedback: prev.unresolvedFeedback + (currentResolved ? 1 : -1)
      } : prev)
    } catch (err) {
      console.error('Failed to update feedback:', err)
    }
  }

  if (!isAdmin) return null

  return (
    <div className="admin-overlay" onClick={onClose}>
      {/* Teachers needs seven columns; 640px gives each of them about 80px,
          which is what made names wrap and the table overflow. Widened for
          that tab only, so the other tabs keep the width they were designed
          for. */}
      <div className={'admin-panel' + (activeTab === 'Teachers' ? ' admin-panel--wide' : '')}
        onClick={e => e.stopPropagation()}>
        <div className="admin-header">
          <button className="admin-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 className="admin-title">Admin Dashboard</h2>
          <button className="admin-refresh" onClick={loadData} title="Refresh">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          </button>
        </div>

        <div className="admin-tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="admin-content">
          {/* Teachers loads its own data through admin-only callables, so it
              does not wait on this dashboard's Firestore reads. */}
          {activeTab === 'Teachers' ? (
            <TeachersTab />
          ) : loading ? (
            <div className="admin-loading">Loading...</div>
          ) : activeTab === 'Overview' ? (
            <OverviewTab stats={stats} />
          ) : activeTab === 'Users' ? (
            <UsersTab users={users} />
          ) : activeTab === 'Analytics' ? (
            <AnalyticsTab activity={activityData} usageStats={usageStatsData} users={users} />
          ) : activeTab === 'Timestamps' ? (
            <TimestampsTab timestampDocs={timestampDocs} />
          ) : (
            <FeedbackTab feedbackList={feedbackList} onToggleResolved={toggleResolved} />
          )}
        </div>
      </div>
    </div>
  )
}

function OverviewTab({ stats }) {
  if (!stats) return null
  const cards = [
    { label: 'Total Users', value: stats.totalUsers },
    { label: 'New (30 days)', value: stats.recentUsers },
    { label: 'Premium', value: stats.premiumUsers },
    { label: 'Total Feedback', value: stats.totalFeedback },
    { label: 'Unresolved', value: stats.unresolvedFeedback },
    { label: 'Avg Rating', value: stats.avgRating },
  ]

  return (
    <div className="admin-stats-grid">
      {cards.map((card, i) => (
        <div key={i} className="admin-stat-card">
          <div className="admin-stat-value">{card.value}</div>
          <div className="admin-stat-label">{card.label}</div>
        </div>
      ))}
    </div>
  )
}

function UsersTab({ users }) {
  return (
    <div className="admin-list">
      {users.map(user => {
        const sub = user.subscription || {}
        const isActive = sub.status === 'active'

        return (
          <div key={user.id} className="admin-user-row">
            <div className="admin-user-info">
              {user.photoURL && <img className="admin-user-avatar" src={user.photoURL} alt="" referrerPolicy="no-referrer" />}
              <div>
                <div className="admin-user-name">{user.displayName || 'No name'}</div>
                <div className="admin-user-email">{user.email}</div>
              </div>
            </div>
            <div className="admin-user-meta">
              {isActive && (
                <span className="admin-badge admin-badge-premium">Premium</span>
              )}
              <span className="admin-user-date">
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        )
      })}
      {users.length === 0 && <div className="admin-empty">No users found</div>}
    </div>
  )
}

function AnalyticsTab({ activity, usageStats, users }) {
  // Aggregate stats
  const totalPlays = activity.length
  const totalSeconds = activity.reduce((sum, a) => sum + (a.playSeconds || 0), 0)
  const completions = activity.filter(a => a.completed).length
  const uniqueUsers = new Set(activity.map(a => a.uid)).size

  // Language breakdown
  const langCounts = {}
  activity.forEach(a => {
    const lang = a.language || 'unknown'
    langCounts[lang] = (langCounts[lang] || 0) + 1
  })

  // Most played lessons (top 10)
  const lessonCounts = {}
  activity.forEach(a => {
    const id = a.sectionId || 'unknown'
    if (!lessonCounts[id]) lessonCounts[id] = { plays: 0, completions: 0, totalPercent: 0 }
    lessonCounts[id].plays++
    if (a.completed) lessonCounts[id].completions++
    lessonCounts[id].totalPercent += (a.maxPercent || 0)
  })
  const topLessons = Object.entries(lessonCounts)
    .sort((a, b) => b[1].plays - a[1].plays)
    .slice(0, 10)

  // Most active users (top 10)
  const userEmailMap = {}
  users.forEach(u => { userEmailMap[u.id] = u.email || u.displayName || u.id })
  const topUsers = [...usageStats]
    .sort((a, b) => (b.totalPlays || 0) - (a.totalPlays || 0))
    .slice(0, 10)

  const formatMinutes = (secs) => {
    if (secs < 60) return `${secs}s`
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `${mins}m`
    return `${Math.floor(mins / 60)}h ${mins % 60}m`
  }

  return (
    <div>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-value">{totalPlays}</div>
          <div className="admin-stat-label">Total Plays</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-value">{completions}</div>
          <div className="admin-stat-label">Completions</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-value">{uniqueUsers}</div>
          <div className="admin-stat-label">Active Users</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-value">{formatMinutes(totalSeconds)}</div>
          <div className="admin-stat-label">Total Listen Time</div>
        </div>
      </div>

      {Object.keys(langCounts).length > 0 && (
        <div className="admin-analytics-section">
          <h3 className="admin-section-title">Language Breakdown</h3>
          <div className="admin-bar-list">
            {Object.entries(langCounts).sort((a, b) => b[1] - a[1]).map(([lang, count]) => (
              <div key={lang} className="admin-bar-row">
                <span className="admin-bar-label">{lang}</span>
                <div className="admin-bar-bg">
                  <div className="admin-bar-fill" style={{ width: `${(count / totalPlays) * 100}%` }} />
                </div>
                <span className="admin-bar-value">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {topLessons.length > 0 && (
        <div className="admin-analytics-section">
          <h3 className="admin-section-title">Most Played Lessons</h3>
          <div className="admin-bar-list">
            {topLessons.map(([id, data]) => (
              <div key={id} className="admin-bar-row">
                <span className="admin-bar-label" title={id}>{id.length > 25 ? id.slice(-25) : id}</span>
                <div className="admin-bar-bg">
                  <div className="admin-bar-fill" style={{ width: `${(data.plays / topLessons[0][1].plays) * 100}%` }} />
                </div>
                <span className="admin-bar-value">{data.plays} ({data.completions} done)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {topUsers.length > 0 && (
        <div className="admin-analytics-section">
          <h3 className="admin-section-title">Most Active Users</h3>
          <div className="admin-bar-list">
            {topUsers.map(u => (
              <div key={u.id} className="admin-bar-row">
                <span className="admin-bar-label">{userEmailMap[u.id] || u.id.slice(0, 12)}</span>
                <div className="admin-bar-bg">
                  <div className="admin-bar-fill" style={{ width: `${((u.totalPlays || 0) / (topUsers[0]?.totalPlays || 1)) * 100}%` }} />
                </div>
                <span className="admin-bar-value">{u.totalPlays || 0} plays</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activity.length === 0 && <div className="admin-empty">No usage data yet. Data will appear once users start playing lessons.</div>}
    </div>
  )
}

function TimestampsTab({ timestampDocs }) {
  // Merge cloud-submitted timestamps over the repo's base structure (keeps category titles).
  const merged = (() => {
    const base = JSON.parse(JSON.stringify(baseTimestamps))
    if (!base.categories) base.categories = []
    for (const d of timestampDocs) {
      if (!d.chant || d.categoryId == null) continue
      let cat = base.categories.find(c => c.id === d.categoryId)
      if (!cat) { cat = { id: d.categoryId, title: `category ${d.categoryId}`, chants: [] }; base.categories.push(cat) }
      if (!cat.chants) cat.chants = []
      const idx = cat.chants.findIndex(ch => ch.id === d.chant.id)
      if (idx >= 0) cat.chants[idx] = d.chant
      else cat.chants.push(d.chant)
    }
    return base
  })()

  const perCategory = merged.categories.map(c => ({
    id: c.id,
    title: c.title,
    total: (c.chants || []).length,
    withMarks: (c.chants || []).filter(ch => ch.timestamps && ch.timestamps.some(t => t !== null)).length
  }))

  const rows = [...timestampDocs].sort((a, b) => {
    const at = a.updatedAt?.seconds || 0, bt = b.updatedAt?.seconds || 0
    return bt - at
  })

  function download() {
    const json = JSON.stringify(merged, null, 2) + '\n'
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'word-timestamps.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="admin-analytics-section">
        <button className="admin-filter-btn active" onClick={download} style={{ padding: '8px 16px' }}>
          &#11015; Download merged word-timestamps.json
        </button>
        <p className="admin-empty" style={{ textAlign: 'left', marginTop: 8 }}>
          {timestampDocs.length} cloud submission(s). Replace <code>src/data/word-timestamps.json</code> with this file and commit.
        </p>
      </div>

      <div className="admin-analytics-section">
        <h3 className="admin-section-title">Coverage by category (base + cloud)</h3>
        <div className="admin-bar-list">
          {perCategory.map(c => (
            <div key={c.id} className="admin-bar-row">
              <span className="admin-bar-label" title={c.title}>{c.title}</span>
              <div className="admin-bar-bg">
                <div className="admin-bar-fill" style={{ width: `${c.total ? (c.withMarks / c.total) * 100 : 0}%` }} />
              </div>
              <span className="admin-bar-value">{c.withMarks} / {c.total}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-analytics-section">
        <h3 className="admin-section-title">Recent submissions</h3>
        <div className="admin-list">
          {rows.map(d => (
            <div key={d.id} className="admin-user-row">
              <div className="admin-user-info">
                <div>
                  <div className="admin-user-name">{d.chant?.id || d.id}</div>
                  <div className="admin-user-email">
                    cat {d.categoryId} &middot; {(d.chant?.timestamps || []).length} marks &middot; {d.markedBy || 'unknown'}
                  </div>
                </div>
              </div>
              <span className="admin-user-date">
                {d.updatedAt?.seconds ? new Date(d.updatedAt.seconds * 1000).toLocaleDateString() : ''}
              </span>
            </div>
          ))}
          {rows.length === 0 && <div className="admin-empty">No cloud submissions yet.</div>}
        </div>
      </div>
    </div>
  )
}

function FeedbackTab({ feedbackList, onToggleResolved }) {
  const [filter, setFilter] = useState('all')

  const filtered = filter === 'all' ? feedbackList
    : filter === 'unresolved' ? feedbackList.filter(f => !f.resolved)
    : feedbackList.filter(f => f.category === filter)

  return (
    <div>
      <div className="admin-filter-row">
        <button className={`admin-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All
        </button>
        <button className={`admin-filter-btn ${filter === 'unresolved' ? 'active' : ''}`} onClick={() => setFilter('unresolved')}>
          Unresolved
        </button>
        {Object.entries(CATEGORY_ICONS).map(([key, icon]) => (
          <button
            key={key}
            className={`admin-filter-btn ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {icon}
          </button>
        ))}
      </div>

      <div className="admin-list">
        {filtered.map(fb => (
          <div key={fb.id} className={`admin-feedback-row ${fb.resolved ? 'resolved' : ''}`}>
            <div className="admin-feedback-top">
              <span className="admin-feedback-cat">
                {CATEGORY_ICONS[fb.category] || '\uD83D\uDCAC'} {fb.category || 'general'}
              </span>
              {fb.rating > 0 && (
                <span className="admin-feedback-rating">
                  {'\u2605'.repeat(fb.rating)}{'\u2606'.repeat(5 - fb.rating)}
                </span>
              )}
              <span className="admin-feedback-date">
                {fb.createdAt ? new Date(fb.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
            {fb.comment && <div className="admin-feedback-comment">{fb.comment}</div>}
            <div className="admin-feedback-bottom">
              <span className="admin-feedback-user">{fb.userName || fb.userEmail || 'Anonymous'}</span>
              {fb.appLanguage && <span className="admin-badge">{fb.appLanguage}</span>}
              <button
                className={`admin-resolve-btn ${fb.resolved ? 'resolved' : ''}`}
                onClick={() => onToggleResolved(fb.id, fb.resolved)}
              >
                {fb.resolved ? '\u2705 Resolved' : 'Mark Resolved'}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="admin-empty">No feedback found</div>}
      </div>
    </div>
  )
}
